import React from "react";
import ErrorScreen from "./ErrorScreen";

/**
 * Page-level boundary for lazy-loaded routes.
 *
 * A lazy chunk fails to load for two reasons: a deploy replaced the build
 * while this tab was open (the new nginx image no longer serves the old chunk
 * hashes), or the network dropped mid-download. Either way React.lazy caches
 * the rejection, so the only recovery is a reload — and that must be the
 * USER's click, never automatic: a forced reload silently discards unsaved
 * work (an estimate mid-edit, a note half-typed). The unsaved-changes guards
 * on those pages still get their say on a user-initiated reload.
 *
 * Without this boundary the failure would climb to RootErrorBoundary and
 * replace the entire admin shell (sidebar, timer bar, sockets) with the
 * crash screen for what is really one page's chunk.
 */
export default class ChunkErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    console.error("[ChunkErrorBoundary]", error, info?.componentStack);
  }

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;
    const updated = Boolean(error?.isChunkLoadError);
    return (
      <ErrorScreen
        code="crash"
        title={updated ? "Chariot was updated" : "This page couldn't load"}
        message={
          updated
            ? "A new version was deployed while this tab was open. Reload to pick it up — if you have unsaved work on another tab, finish saving there first."
            : "The page's files didn't finish downloading (usually a connection hiccup). Reload to try again."
        }
        primary={{ label: "Reload", onClick: () => window.location.reload() }}
        secondary={{ label: "Go to dashboard", onClick: () => { window.location.href = "/app/dashboard"; } }}
      />
    );
  }
}
